import { TaskActionBar, type TaskAction } from '@/features/contribution-kit';
import type { SpeakContributeCopy } from '../speak-contribute-copy';

type SpeakActionBarProps = {
  t: { previous: string; next: string; resumeRecording: string; pauseRecording: string };
  copy: SpeakContributeCopy;
  isRecording: boolean;
  isPaused: boolean;
  isUploading: boolean;
  alreadyDone: boolean;
  hasRecording: boolean;
  isLastSentence: boolean;
  canGoPrevious: boolean;
  hasSentences: boolean;
  shortcutsEnabled: boolean;
  onToggleRecording: () => void;
  onTogglePause: () => void;
  onPrevious: () => void;
  onValidateNext: () => void;
};

/**
 * Bottom bar of "Enregistrer". Same handlers as before, laid out by the
 * state of the take:
 * - idle: previous, record, next (moves on without sending, as before);
 * - recording: pause / resume, stop;
 * - take ready: previous, redo (records again over it), send and continue;
 * - phrase already sent: previous, next.
 */
export function SpeakActionBar({
  t,
  copy,
  isRecording,
  isPaused,
  isUploading,
  alreadyDone,
  hasRecording,
  isLastSentence,
  canGoPrevious,
  hasSentences,
  shortcutsEnabled,
  onToggleRecording,
  onTogglePause,
  onPrevious,
  onValidateNext,
}: SpeakActionBarProps) {
  // translations.ts has no top-level `finish` (the old card showed an empty
  // label on the last phrase), so the word comes from the page copy.
  const nextLabel = isLastSentence ? copy.finish : t.next;
  const previous: TaskAction = {
    key: 'previous',
    label: t.previous,
    icon: 'arrowLeft',
    onClick: onPrevious,
    disabled: !canGoPrevious,
    shortcut: 'ArrowLeft',
  };

  let actions: TaskAction[];
  if (isRecording) {
    actions = [
      { key: 'pause', label: isPaused ? t.resumeRecording : t.pauseRecording, icon: isPaused ? 'play' : 'pause', onClick: onTogglePause, shortcut: 'p', testId: 'speak-pause' },
      { key: 'stop', label: copy.stop, icon: 'square', tone: 'danger', onClick: onToggleRecording, shortcut: ' ', testId: 'speak-record' },
    ];
  } else if (alreadyDone) {
    actions = [
      previous,
      { key: 'next', label: nextLabel, icon: isLastSentence ? 'checkCircle' : 'arrowRight', tone: 'primary', onClick: onValidateNext, disabled: !hasSentences, loading: isUploading, shortcut: 'Enter' },
    ];
  } else if (hasRecording) {
    actions = [
      previous,
      { key: 'redo', label: copy.redo, icon: 'refreshCw', onClick: onToggleRecording, disabled: isUploading, shortcut: 'r', testId: 'speak-record' },
      { key: 'send', label: copy.send, icon: 'send', tone: 'primary', onClick: onValidateNext, disabled: !hasSentences, loading: isUploading, shortcut: 'Enter', testId: 'speak-send' },
    ];
  } else {
    actions = [
      previous,
      { key: 'record', label: copy.record, icon: 'mic', tone: 'primary', onClick: onToggleRecording, disabled: isUploading, shortcut: ' ', testId: 'speak-record' },
      { key: 'next', label: nextLabel, icon: 'arrowRight', onClick: onValidateNext, disabled: !hasSentences, loading: isUploading, shortcut: 'ArrowRight' },
    ];
  }

  return (
    <TaskActionBar actions={actions} shortcutsEnabled={shortcutsEnabled} />
  );
}
